import React, { useState, useEffect } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { Plus, Tag, Eye, EyeOff, Pencil, Check, X, Armchair, Printer } from "lucide-react";

// Módulos de la plataforma que una categoría puede activar para sus empresas.
const PLATFORM_MODULES = [
  { key: "mesas", label: "Mesas", description: "Plano de mesas, comandas, venta rápida y facturas", icon: Armchair },
  { key: "impresoras", label: "Impresoras", description: "Estaciones de impresión y puente de la sede", icon: Printer },
];

function toSlug(str) {
  return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");
}

function IconPreview({ icon, size = "text-2xl" }) {
  if (!icon) return <Tag size={20} className="text-gray-400" />;
  // Si parece un nombre de componente (solo letras y mayúscula inicial) es Lucide; si no, es emoji
  if (/^[A-Z][a-zA-Z0-9]+$/.test(icon)) {
    return <Tag size={20} className="text-gray-400" />;
  }
  return <span className={`${size} leading-none`}>{icon}</span>;
}

export default function CategoryManager() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", description: "", icon: "" });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchCategories = async () => {
    try {
      const res = await API.get("/categories?all=true");
      setCategories(res.data?.data?.categories ?? res.data?.data ?? []);
    } catch {
      toast.error("Error al obtener categorías");
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const createCategory = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) { toast.warn("Nombre y slug son obligatorios"); return; }
    try {
      setLoading(true);
      await API.post("/categories", { ...form, active: true });
      toast.success("Categoría creada");
      setForm({ name: "", slug: "", description: "", icon: "" });
      fetchCategories();
    } catch {
      toast.error("Error al crear categoría");
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (cat) => {
    try {
      await API.put(`/categories/${cat.id}`, { active: !cat.active });
      toast.success(cat.active ? "Categoría desactivada" : "Categoría activada");
      fetchCategories();
    } catch {
      toast.error("Error al actualizar categoría");
    }
  };

  const toggleFeature = async (cat, feature) => {
    const current = cat.features ?? [];
    const next = current.includes(feature) ? current.filter((f) => f !== feature) : [...current, feature];
    try {
      await API.put(`/categories/${cat.id}/features`, { features: next });
      toast.success("Módulos actualizados");
      fetchCategories();
    } catch {
      toast.error("Error al actualizar los módulos");
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditForm({ name: cat.name, slug: cat.slug, description: cat.description ?? "", icon: cat.icon ?? "" });
  };

  const saveEdit = async (id) => {
    try {
      await API.put(`/categories/${id}`, editForm);
      toast.success("Categoría actualizada");
      setEditingId(null);
      fetchCategories();
    } catch {
      toast.error("Error al actualizar categoría");
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Categorías</h2>
        <p className="text-gray-500 text-sm">
          Administra las categorías del landing. Las inactivas no aparecen en la tienda.
        </p>
      </div>

      {/* ── Formulario crear ─────────────────────────────────────────────────── */}
      <form onSubmit={createCategory} className="bg-white shadow-md rounded-2xl p-6 mb-8 grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Nombre *</label>
          <input
            placeholder="Ej: Heladerías"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value, slug: toSlug(e.target.value) })}
            className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Slug *</label>
          <input
            placeholder="heladerias"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: toSlug(e.target.value) })}
            className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Ícono (emoji)</label>
          <div className="flex items-center gap-2 border border-gray-300 rounded-xl px-3 py-2">
            <input
              placeholder="🍦"
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
              className="flex-1 text-sm outline-none"
            />
            <IconPreview icon={form.icon} />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Descripción</label>
          <input
            placeholder="Opcional"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
          />
        </div>
        <button
          disabled={loading}
          className="lg:col-span-4 bg-supernova-500 hover:bg-supernova-600 text-white rounded-xl py-2.5 flex justify-center items-center gap-2 text-sm font-semibold transition"
        >
          <Plus size={16} />
          {loading ? "Creando..." : "Crear Categoría"}
        </button>
      </form>

      {/* ── Lista ────────────────────────────────────────────────────────────── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {categories.map((cat, i) => (
          <motion.div
            key={cat.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className={`bg-white rounded-2xl shadow p-4 flex flex-col gap-3 transition-all ${!cat.active ? "opacity-50" : ""}`}
          >
            {editingId === cat.id ? (
              /* ── Modo edición ── */
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <input
                    value={editForm.icon}
                    onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })}
                    className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-center text-lg"
                    placeholder="🍦"
                  />
                  <input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value, slug: toSlug(e.target.value) })}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  />
                </div>
                <input
                  value={editForm.slug}
                  onChange={(e) => setEditForm({ ...editForm, slug: toSlug(e.target.value) })}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs text-gray-500 focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  placeholder="slug"
                />
                <input
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                  placeholder="Descripción (opcional)"
                />
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(cat.id)} className="flex-1 flex items-center justify-center gap-1 bg-green-500 hover:bg-green-600 text-white rounded-lg py-1.5 text-xs font-semibold">
                    <Check size={13} /> Guardar
                  </button>
                  <button onClick={() => setEditingId(null)} className="flex-1 flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg py-1.5 text-xs font-semibold">
                    <X size={13} /> Cancelar
                  </button>
                </div>
              </div>
            ) : (
              /* ── Vista normal ── */
              <>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-supernova-50 flex items-center justify-center flex-shrink-0">
                    <IconPreview icon={cat.icon} size="text-2xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-800 truncate">{cat.name}</p>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cat.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {cat.active ? "Activa" : "Inactiva"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">{cat.slug}</p>
                    {cat.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{cat.description}</p>}
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">Módulos para sus empresas</p>
                  <div className="flex flex-col gap-2">
                    {PLATFORM_MODULES.map((m) => {
                      const on = (cat.features ?? []).includes(m.key);
                      return (
                        <label key={m.key} className="flex items-center justify-between gap-2 cursor-pointer">
                          <span className="flex items-center gap-2 min-w-0">
                            <m.icon size={14} className={on ? "text-supernova-600" : "text-gray-400"} />
                            <span className="text-xs font-semibold text-gray-700">{m.label}</span>
                            <span className="text-[10px] text-gray-400 truncate hidden sm:inline">{m.description}</span>
                          </span>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggleFeature(cat, m.key)}
                            className="accent-supernova-500 w-4 h-4 shrink-0"
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(cat)}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold transition ${
                      cat.active
                        ? "bg-red-50 hover:bg-red-100 text-red-600"
                        : "bg-green-50 hover:bg-green-100 text-green-700"
                    }`}
                  >
                    {cat.active ? <><EyeOff size={13} /> Desactivar</> : <><Eye size={13} /> Activar</>}
                  </button>
                  <button
                    onClick={() => startEdit(cat)}
                    className="flex items-center justify-center gap-1.5 px-3 rounded-xl py-2 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 transition"
                  >
                    <Pencil size={13} /> Editar
                  </button>
                </div>
              </>
            )}
          </motion.div>
        ))}
      </div>

      {categories.length === 0 && (
        <p className="text-gray-500 text-center mt-10">No hay categorías registradas.</p>
      )}
    </div>
  );
}
